import { useState, useEffect, useContext, useMemo } from 'react';
import { getMemberships, getMyActiveMembership, getMyOffers, createMembershipCheckout, getBillingPortalUrl, triggerMembershipSync, addDgeAddon } from '../services/membership.service';
import { MEMBERSHIP_PRICE_TYPE, MEMBERSHIP_TYPE } from '../enums/membership.enum';
import { MembershipModel, MembershipOfferModel, UserMembershipModel } from '../models/membership.model';
import { CompareTable, MembershipCard, mapMembershipToTier, TIER_COLOR_MAP } from '../components/memberships';
import type { BillingPeriod, MembershipTier, MembershipAction } from '../components/memberships';
import { TIER_SORT } from '../constants/membership.constants';
import { UserContext } from '../contexts/user.context';
import { UserProviderValue } from '../models/user-provider.model';
import { Modal, Animation, LoadingSymbol } from '../components/ui';
import { categorizeMemberships, formatPriceType } from '../utils/membership.utils';
import './MembershipsPage.css';

const isMobileDevice = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const GOLFN_APP_DEEP_LINK = 'golfn://home';
const GOLFN_APP_STORE_ANDROID = 'https://play.google.com/store/apps/details?id=com.golfn.app';
const GOLFN_APP_STORE_IOS = 'https://apps.apple.com/app/golfn/id6502394298';

const openGolfnApp = () => {
   window.location.href = GOLFN_APP_DEEP_LINK;
   // Fallback to store if deep link doesn't open the app
   setTimeout(() => {
      if (/Android/i.test(navigator.userAgent)) {
         window.location.href = GOLFN_APP_STORE_ANDROID;
      } else {
         window.location.href = GOLFN_APP_STORE_IOS;
      }
   }, 1500);
};

function getAction(
   activeMembership: UserMembershipModel | null,
   tier: MembershipTier,
   billing: BillingPeriod
): MembershipAction {
   if (!activeMembership?.membershipType) return null;

   const activeSort = TIER_SORT[activeMembership.membershipType] ?? 0;
   const tierSort = TIER_SORT[tier.type] ?? 0;

   if (tierSort > activeSort) return 'upgrade';
   if (tierSort < activeSort) return 'downgrade';

   // Same tier — compare billing period
   const activeBilling = activeMembership.type;
   const newBilling = billing === 'annual' ? 'annually' : 'monthly';
   if (activeBilling === newBilling) return 'current';
   if (activeBilling === 'annually' && newBilling === 'monthly') return 'downgrade';
   if (activeBilling === 'monthly' && newBilling === 'annually') return 'upgrade';

   return 'current';
}

export function MembershipsPage() {
   const { isAuthenticated, setIsLoginModalOpen, user } = useContext<UserProviderValue>(UserContext);
   const [billing, setBilling] = useState<BillingPeriod>('annual');
   const [showDiamond, setShowDiamond] = useState(false);
   const [tiers, setTiers] = useState<MembershipTier[]>([]);
   const [diamondTier, setDiamondTier] = useState<MembershipTier | null>(null);
   const [loading, setLoading] = useState(true);
   const [checkoutLoading, setCheckoutLoading] = useState(false);
   const [successScreen, setSuccessScreen] = useState(false);
   const [allActiveMemberships, setAllActiveMemberships] = useState<UserMembershipModel[]>([]);
   const [offers, setOffers] = useState<MembershipOfferModel[]>([]);
   const [downgradeScreen, setDowngradeScreen] = useState(false);
   const [errorMessage, setErrorMessage] = useState<string | null>(null);
   const [confirmAnimation, setConfirmAnimation] = useState<any>(null);
   const [cancelAnimation, setCancelAnimation] = useState<any>(null);
   const [membershipLoading, setMembershipLoading] = useState(false);
   const [isMobile] = useState(() => isMobileDevice());
   const [dgeSelected, setDgeSelected] = useState(false);
   const [dgeLoading, setDgeLoading] = useState(false);
   const [dgeConfirmScreen, setDgeConfirmScreen] = useState(false);

   // Load Lottie animations
   useEffect(() => {
      fetch('/assets/animations/confirm.json')
         .then((res) => res.json())
         .then(setConfirmAnimation)
         .catch(() => {});
      fetch('/assets/animations/cancel.json')
         .then((res) => res.json())
         .then(setCancelAnimation)
         .catch(() => {});
   }, []);

   // Reset DGE selection when billing period changes
   useEffect(() => {
      setDgeSelected(false);
   }, [billing]);

   // Handle ?success=true redirect from Stripe
   useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('success') === 'true') {
         setSuccessScreen(true);
         window.history.replaceState({}, '', window.location.pathname);
      }
   }, []);

   // Load memberships
   useEffect(() => {
      getMemberships().then((res) => {
         if (res.data) {
            const memberships = res.data as MembershipModel[];
            const mapped: MembershipTier[] = [];
            let diamond: MembershipTier | null = null;

            for (const m of memberships) {
               if (m.type === MEMBERSHIP_TYPE.GREEN) continue;

               const tier = mapMembershipToTier(m);
               if (!tier) continue;

               if (m.type === MEMBERSHIP_TYPE.DIAMOND) {
                  diamond = tier;
               } else {
                  mapped.push(tier);
               }
            }

            setTiers(mapped);
            setDiamondTier(diamond);
         }
         setLoading(false);
      });
   }, []);

   const refreshActiveMembership = () => {
      if (!isAuthenticated) {
         setAllActiveMemberships([]);
         setOffers([]);
         return;
      }
      getMyActiveMembership().then((res) => {
         if (res.data && Array.isArray(res.data) && res.data.length > 0) {
            setAllActiveMemberships(res.data as UserMembershipModel[]);
         } else {
            setAllActiveMemberships([]);
         }
      });
      getMyOffers().then((res) => {
         if (res.data && Array.isArray(res.data)) {
            setOffers(res.data as MembershipOfferModel[]);
         } else {
            setOffers([]);
         }
      });
   };

   // Load active membership when authenticated
   useEffect(() => {
      refreshActiveMembership();
   }, [isAuthenticated]);

   // Categorize memberships into primary + one-round boosts
   const { primary: primaryMembership, oneRoundBoosts } = useMemo(
      () => categorizeMemberships(allActiveMemberships),
      [allActiveMemberships]
   );

   // Only use the primary membership for card actions if it's a recurring plan (not one-round/one-day)
   const isOneRoundPrimary = primaryMembership?.type === MEMBERSHIP_PRICE_TYPE.ONE_ROUND || primaryMembership?.type === MEMBERSHIP_PRICE_TYPE.ONE_DAY;
   const actionMembership = isOneRoundPrimary ? null : primaryMembership;

   // Check if user already has DGE addon
   const hasDge = useMemo(
      () => allActiveMemberships.some((m) => m.membershipType === MEMBERSHIP_TYPE.DAILY_GRIND_ELITE),
      [allActiveMemberships]
   );

   // Filter offers: only show if user has no recurring plan or the offer tier is higher
   const visibleOffers = useMemo(() => {
      if (!offers.length) return [];
      const primarySort = actionMembership?.membershipSort ?? 0;
      return offers.filter((o) => o.membershipSort > primarySort);
   }, [offers, actionMembership]);

   const handleClaimOffer = async (offer: MembershipOfferModel) => {
      if (!isAuthenticated) {
         setIsLoginModalOpen(true);
         return;
      }
      if (checkoutLoading) return;

      setCheckoutLoading(true);
      try {
         const res = await createMembershipCheckout(offer.membershipId, offer.priceType, offer.id);
         if (res.data?.downgrade) {
            setDowngradeScreen(true);
         } else if (res.data?.url) {
            window.location.href = res.data.url;
         } else if (res.error) {
            setErrorMessage(res.error.message || 'Failed to claim offer');
         }
      } catch {
         setErrorMessage('Something went wrong. Please try again.');
      } finally {
         setCheckoutLoading(false);
      }
   };

   const tiersWithActions = tiers.map((tier) => ({
      ...tier,
      action: isAuthenticated ? getAction(actionMembership, tier, billing) : null,
   }));

   const diamondWithAction = diamondTier
      ? { ...diamondTier, action: isAuthenticated ? getAction(actionMembership, diamondTier, billing) : null }
      : null;

   const allTiersForCompare = diamondWithAction ? [...tiersWithActions, diamondWithAction] : tiersWithActions;

   const handleBecomeMember = async (membershipId?: string) => {
      if (!isAuthenticated) {
         setIsLoginModalOpen(true);
         return;
      }

      if (!membershipId || checkoutLoading) return;

      const priceType = billing === 'annual' ? 'annually' : 'monthly';
      setCheckoutLoading(true);

      try {
         const res = await createMembershipCheckout(membershipId, priceType, undefined, dgeSelected || undefined);
         if (res.data?.dgeAddonApplied) {
            // DGE addon was applied to existing subscription
            setSuccessScreen(true);
            setDgeSelected(false);
         } else if (res.data?.downgrade) {
            setDowngradeScreen(true);
         } else if (res.data?.url) {
            window.location.href = res.data.url;
         } else if (res.error) {
            setErrorMessage(res.error.message || 'Failed to process membership change');
         }
      } catch {
         setErrorMessage('Something went wrong. Please try again.');
      } finally {
         setCheckoutLoading(false);
      }
   };

   const handleAddDgeAddon = () => {
      if (!isAuthenticated || dgeLoading) return;
      setDgeConfirmScreen(true);
   };

   const confirmAddDgeAddon = async () => {
      setDgeConfirmScreen(false);
      setDgeLoading(true);
      try {
         const res = await addDgeAddon();
         if (res.data?.dgeAddonApplied) {
            setSuccessScreen(true);
            triggerMembershipSync();
            // Keep dgeLoading true — will be cleared after poll completes
         } else {
            setDgeLoading(false);
            if (res.error) {
               setErrorMessage(res.error.message || 'Failed to add Daily Grind Elite addon');
            }
         }
      } catch {
         setDgeLoading(false);
         setErrorMessage('Something went wrong. Please try again.');
      }
   };

   const pollActiveMembership = (retries = 8, interval = 3000) => {
      setMembershipLoading(true);
      // Snapshot current state to detect changes
      const prevPrimaryType = primaryMembership?.membershipType;
      const prevHasDge = allActiveMemberships.some((m) => m.membershipType === MEMBERSHIP_TYPE.DAILY_GRIND_ELITE);
      const prevCount = allActiveMemberships.length;
      let attempt = 0;
      const poll = () => {
         getMyActiveMembership().then((res) => {
            if (res.data && Array.isArray(res.data) && res.data.length > 0) {
               const newMemberships = res.data as UserMembershipModel[];
               const newPrimaryType = newMemberships.find((m) => m.stripeSubscriptionId && m.membershipType !== MEMBERSHIP_TYPE.DAILY_GRIND_ELITE)?.membershipType;
               const newHasDge = newMemberships.some((m) => m.membershipType === MEMBERSHIP_TYPE.DAILY_GRIND_ELITE);
               const changed = newPrimaryType !== prevPrimaryType || newHasDge !== prevHasDge || newMemberships.length !== prevCount;

               if (changed || attempt >= retries - 1) {
                  setAllActiveMemberships(newMemberships);
                  setMembershipLoading(false);
               } else if (++attempt < retries) {
                  setTimeout(poll, interval);
               } else {
                  setAllActiveMemberships(newMemberships);
                  setMembershipLoading(false);
               }
            } else if (++attempt < retries) {
               setTimeout(poll, interval);
            } else {
               setMembershipLoading(false);
            }
         }).catch(() => {
            if (++attempt < retries) {
               setTimeout(poll, interval);
            } else {
               setMembershipLoading(false);
            }
         });
      };
      // First attempt after a short delay for webhook processing
      setTimeout(poll, 2000);
   };

   const handleSuccessClose = () => {
      setSuccessScreen(false);
      if (dgeLoading) {
         // DGE addon was just applied — wait for webhook to sync before polling
         setTimeout(() => {
            pollActiveMembership();
            setDgeLoading(false);
         }, 6000);
      } else {
         pollActiveMembership();
      }
      triggerMembershipSync();
   };

   const handleDowngradeClose = () => {
      setDowngradeScreen(false);
      refreshActiveMembership();
   };

   const handleErrorClose = () => {
      setErrorMessage(null);
   };

   if (loading || tiers.length === 0) {
      return (
         <div className="memberships-page">
            <div style={{ display: 'flex', justifyContent: 'center', padding: '120px 0' }}>
               <LoadingSymbol size={36} />
            </div>
         </div>
      );
   }

   return (
      <div className="memberships-page">
         <h1 className="hero-heading">Every round earns more with a Membership</h1>

         {visibleOffers.map((offer) => (
            <div key={offer.id} className="offer-banner">
               <div className="offer-banner-content">
                  <span className="offer-banner-dot" style={{ background: TIER_COLOR_MAP[offer.membershipType] ?? '#fff' }} />
                  <div className="offer-banner-text">
                     <span className="offer-banner-title">
                        {offer.discountPercent >= 100
                           ? `Free ${offer.membershipName} for ${offer.discountDurationMonths} month${offer.discountDurationMonths > 1 ? 's' : ''}`
                           : `${offer.discountPercent}% off ${offer.membershipName} for ${offer.discountDurationMonths} month${offer.discountDurationMonths > 1 ? 's' : ''}`
                        }
                     </span>
                     {offer.referredBy && (
                        <span className="offer-banner-sub">Referred by {offer.referredBy.username}</span>
                     )}
                  </div>
               </div>
               <button
                  className="offer-banner-btn"
                  disabled={checkoutLoading}
                  onClick={() => handleClaimOffer(offer)}
               >
                  {checkoutLoading ? 'Loading...' : 'Claim Offer'}
               </button>
            </div>
         ))}

         {membershipLoading && !primaryMembership?.membershipType && (
            <div className="current-plan-box">
               <p className="current-plan-title">Current Membership</p>
               <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                  <LoadingSymbol size={24} />
               </div>
               <p className="membership-loading-text">Setting up your membership…</p>
            </div>
         )}

         {primaryMembership?.membershipType && (
            <div className="current-plan-box">
               <p className="current-plan-title">Current Membership</p>
               <div className="current-plan-header">
                  <div className="current-plan-tier">
                     <span className="current-plan-dot" style={{ background: TIER_COLOR_MAP[primaryMembership.membershipType] ?? '#fff' }} />
                     <span className="current-plan-name">
                        {primaryMembership.membershipType.charAt(0) + primaryMembership.membershipType.slice(1).toLowerCase()}{' '}
                        {formatPriceType(primaryMembership.type)}
                     </span>
                  </div>
                  <span className="current-plan-status">Active</span>
               </div>

               <div className="current-plan-details">
                  {primaryMembership.isValidUntil && (
                     <div className="current-plan-detail">
                        <span className="current-plan-detail-label">
                           {primaryMembership.nextPayment ? 'Renews on' : 'Valid until'}
                        </span>
                        <span className="current-plan-detail-value">
                           {new Date(primaryMembership.isValidUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                     </div>
                  )}
                  {primaryMembership.nextPayment && (
                     <div className="current-plan-detail">
                        <span className="current-plan-detail-label">Auto-renews</span>
                        <span className="current-plan-detail-value current-plan-auto-renew">Yes</span>
                     </div>
                  )}
                  {!primaryMembership.nextPayment && primaryMembership.isValidUntil && (
                     <div className="current-plan-detail">
                        <span className="current-plan-detail-label">Auto-renews</span>
                        <span className="current-plan-detail-value" style={{ color: '#8E9191' }}>No</span>
                     </div>
                  )}
               </div>

               {primaryMembership.upcomingMeta && (
                  <div className="current-plan-upcoming">
                     {primaryMembership.upcomingMeta.nextAction === 'downgrade' && primaryMembership.upcomingMeta.membershipType && (
                        <span>
                           Switching to{' '}
                           <strong>
                              {primaryMembership.upcomingMeta.membershipType.charAt(0) + primaryMembership.upcomingMeta.membershipType.slice(1).toLowerCase()}{' '}
                              {primaryMembership.upcomingMeta.type === 'annually' ? 'Annual' : 'Monthly'}
                           </strong>
                           {primaryMembership.upcomingMeta.startDate && (
                              <> on {new Date(primaryMembership.upcomingMeta.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                           )}
                        </span>
                     )}
                     {primaryMembership.upcomingMeta.nextAction === 'cancel' && (
                        <span>Your membership will be cancelled at the end of the current billing period.</span>
                     )}
                  </div>
               )}

               <div className="current-plan-actions">
                  {primaryMembership.stripeSubscriptionId && (
                     <button
                        className="current-plan-manage-btn"
                        onClick={async () => {
                           const res = await getBillingPortalUrl();
                           if (res.data?.url) {
                              window.open(res.data.url, '_blank');
                           }
                        }}
                     >
                        Manage Subscription
                     </button>
                  )}
                  {isMobile && (
                     <button
                        className="current-plan-open-app-btn"
                        onClick={openGolfnApp}
                     >
                        Start Playing
                     </button>
                  )}
               </div>

               {/* Daily Grind Elite toggle — hidden for now
               {primaryMembership.membershipType === MEMBERSHIP_TYPE.WHITE && primaryMembership.stripeSubscriptionId && (
                  <div className="current-plan-dge-toggle">
                     <div className="feature-row">
                        <div className="feature-text">
                           <span className="feature-name dge-name">Daily Grind Elite</span>
                           <span className="feature-sub">{hasDge ? 'Included in your subscription' : 'Add to your White membership'}</span>
                        </div>
                        {hasDge ? (
                           <img className="feature-check" src="/assets/icons/memberships/check-feature.svg" alt="" width={19} height={12} />
                        ) : dgeLoading ? (
                           <LoadingSymbol size={20} />
                        ) : (
                           <div className="dge-right">
                              <div
                                 className="toggle-track"
                                 onClick={handleAddDgeAddon}
                                 role="switch"
                                 aria-checked={false}
                                 style={{ cursor: 'pointer' }}
                              >
                                 <div className="toggle-knob" />
                              </div>
                              <span className="toggle-price">+$9.99/mo</span>
                           </div>
                        )}
                     </div>
                  </div>
               )}
               */}

               {oneRoundBoosts.length > 0 && (
                  <div className="current-plan-boosts">
                     <p className="current-plan-boosts-title">Upcoming Round Boosts</p>
                     {oneRoundBoosts.map((boost) => (
                        <div key={boost.membershipType} className="current-plan-boost-row">
                           <span className="current-plan-dot" style={{ background: TIER_COLOR_MAP[boost.membershipType] ?? '#fff' }} />
                           <span className="current-plan-boost-name">
                              {boost.membershipType.charAt(0) + boost.membershipType.slice(1).toLowerCase()}
                           </span>
                           <span className="current-plan-boost-count">
                              {boost.count} round{boost.count > 1 ? 's' : ''}
                           </span>
                        </div>
                     ))}
                  </div>
               )}
            </div>
         )}

         <div className="billing-toggle">
            <button className={billing === 'annual' ? 'active' : ''} onClick={() => setBilling('annual')}>
               Annual <span className="save-badge">Save 25%</span>
            </button>
            <button className={billing === 'monthly' ? 'active' : ''} onClick={() => setBilling('monthly')}>
               Monthly
            </button>
         </div>

         <div className="membership-cards">
            {tiersWithActions.map((tier) => (
               <MembershipCard
                  key={tier.id}
                  tier={tier}
                  billing={billing}
                  onBecomeMember={handleBecomeMember}
                  checkoutLoading={checkoutLoading}
                  dgeEnabled={tier.type === MEMBERSHIP_TYPE.WHITE ? (hasDge || dgeSelected) : undefined}
                  onDgeToggle={tier.type === MEMBERSHIP_TYPE.WHITE && !hasDge ? () => setDgeSelected(!dgeSelected) : undefined}
               />
            ))}
         </div>

         <div className="diamond-section">
            <span className="diamond-label">Looking for our top-tier Diamond membership?</span>
            <button className="diamond-link" onClick={() => setShowDiamond(!showDiamond)}>
               {showDiamond ? 'Hide' : 'Show'}
            </button>
         </div>

         {showDiamond && diamondWithAction && (
            <div className="diamond-card-wrapper">
               <MembershipCard tier={diamondWithAction} billing={billing} onBecomeMember={handleBecomeMember} checkoutLoading={checkoutLoading} />
            </div>
         )}

         <CompareTable tiers={allTiersForCompare} billing={billing} onBecomeMember={handleBecomeMember} checkoutLoading={checkoutLoading} />

         {/* Success Modal — shown after Stripe checkout redirect */}
         <Modal
            className="membership-result-modal"
            active={successScreen}
            onClose={handleSuccessClose}
            darkBackground
            hideCloseButton
            button={{ type: 'white-big', title: 'OK', onClick: handleSuccessClose }}
         >
            <div className="membership-result-content">
               <div className="membership-result-icon">
                  {confirmAnimation && (
                     <Animation animationData={confirmAnimation} loop={false} style={{ width: '100px', height: '100px' }} delay={200} />
                  )}
               </div>
               <h2 className="membership-result-title">Membership Successfully Activated</h2>
               <p className="membership-result-message">You're all set to start earning more with your new membership.</p>
               {isMobile && (
                  <button className="membership-result-open-app-btn" onClick={() => {
                     triggerMembershipSync();
                     openGolfnApp();
                  }}>
                     Start Playing
                  </button>
               )}
            </div>
         </Modal>

         {/* Downgrade Modal — shown after scheduling a downgrade */}
         <Modal
            className="membership-result-modal"
            active={downgradeScreen}
            onClose={handleDowngradeClose}
            darkBackground
            hideCloseButton
            button={{ type: 'white-big', title: 'OK', onClick: handleDowngradeClose }}
         >
            <div className="membership-result-content">
               <div className="membership-result-icon">
                  {confirmAnimation && (
                     <Animation animationData={confirmAnimation} loop={false} style={{ width: '100px', height: '100px' }} delay={200} />
                  )}
               </div>
               <h2 className="membership-result-title">Change Scheduled</h2>
               <p className="membership-result-message">Your new plan will take effect at the end of your current billing period.</p>
            </div>
         </Modal>

         {/* Error Modal */}
         <Modal
            className="membership-result-modal"
            active={!!errorMessage}
            onClose={handleErrorClose}
            darkBackground
            hideCloseButton
            button={{ type: 'white-big', title: 'OK', onClick: handleErrorClose }}
         >
            <div className="membership-result-content">
               <div className="membership-result-icon">
                  {cancelAnimation && (
                     <Animation animationData={cancelAnimation} loop={false} style={{ width: '100px', height: '100px' }} delay={200} />
                  )}
               </div>
               <h2 className="membership-result-title">Something went wrong</h2>
               <p className="membership-result-message">{errorMessage}</p>
            </div>
         </Modal>

         {/* DGE Addon Confirm Modal */}
         <Modal
            className="membership-result-modal"
            active={dgeConfirmScreen}
            onClose={() => setDgeConfirmScreen(false)}
            darkBackground
            hideCloseButton
         >
            <div className="membership-result-content">
               <h2 className="membership-result-title">Add Daily Grind Elite</h2>
               <p className="membership-result-message">
                  Are you sure you want to add Daily Grind Elite to your membership for $9.99/mo? Your payment method on file will be automatically charged a prorated amount for the remainder of your current billing period.
               </p>
               <div className="dge-confirm-buttons">
                  <button className="dge-confirm-cancel" onClick={() => setDgeConfirmScreen(false)}>Cancel</button>
                  <button className="dge-confirm-add" onClick={confirmAddDgeAddon}>Add Elite</button>
               </div>
            </div>
         </Modal>
      </div>
   );
}
